import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, Users, Check, Gift, Zap, ArrowRight, Shield, Clock, CreditCard, ChevronRight } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({
    nom_entreprise: '',
    siret: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    acceptCGV: false
  });

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const validateForm = () => {
    if (!form.nom_entreprise.trim()) {
      toast.error("Le nom de l'entreprise est requis");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("L'email est requis");
      return false;
    }
    if (!form.password || form.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return false;
    }
    if (!form.acceptCGV) {
      toast.error("Vous devez accepter les CGV");
      return false;
    }
    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await api.registerCompany({
        nom_entreprise: form.nom_entreprise,
        siret: form.siret || null,
        email: form.email,
        password: form.password,
        telephone: form.telephone || null,
        nombre_licences: 1, // Will be ignored - trial gives 1 free
        billing_plan: 'monthly' // Default, will be set when buying
      });
      
      toast.success('Compte créé ! Connexion en cours...');
      
      // Auto-login
      const loginResponse = await api.login({
        email: form.email,
        password: form.password
      });
      
      localStorage.setItem('token', loginResponse.data.access_token);
      
      // Redirect to admin dashboard
      navigate('/admin');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">CoproEnergy</h1>
          </div>
          <p className="text-slate-400">Plateforme de gestion commerciale pour la rénovation énergétique</p>
        </div>

        {/* Trial Banner */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-4 mb-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Essai gratuit 7 jours</h3>
            <p className="text-emerald-100 text-sm">
              1 licence offerte pour tester la plateforme. Sans carte bancaire.
            </p>
          </div>
        </div>

        {/* Registration Card */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Créer votre compte</CardTitle>
            <CardDescription className="text-slate-400">
              Accédez immédiatement à votre espace de gestion
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Company Name */}
            <div>
              <Label className="text-slate-300">Nom de l'entreprise *</Label>
              <Input
                value={form.nom_entreprise}
                onChange={(e) => handleChange('nom_entreprise', e.target.value)}
                placeholder="Ma Société SAS"
                className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* SIRET (optional) */}
            <div>
              <Label className="text-slate-300">SIRET <span className="text-slate-500">(optionnel)</span></Label>
              <Input
                value={form.siret}
                onChange={(e) => handleChange('siret', e.target.value)}
                placeholder="123 456 789 00012"
                className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Email */}
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contact@entreprise.fr"
                className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Phone */}
            <div>
              <Label className="text-slate-300">Téléphone <span className="text-slate-500">(optionnel)</span></Label>
              <Input
                type="tel"
                value={form.telephone}
                onChange={(e) => handleChange('telephone', e.target.value)}
                placeholder="01 23 45 67 89"
                className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Mot de passe *</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Min. 6 caractères"
                  className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label className="text-slate-300">Confirmer *</Label>
                <Input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  placeholder="Confirmer"
                  className="mt-1 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </div>

            {/* CGV Checkbox */}
            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                id="cgv"
                checked={form.acceptCGV}
                onChange={(e) => handleChange('acceptCGV', e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-700 text-emerald-600 focus:ring-emerald-500"
              />
              <label htmlFor="cgv" className="text-sm text-slate-400">
                J'accepte les{' '}
                <Link to="/cgv" className="text-emerald-400 hover:underline" target="_blank">
                  Conditions Générales de Vente
                </Link>{' '}
                et la{' '}
                <Link to="/confidentialite" className="text-emerald-400 hover:underline" target="_blank">
                  Politique de Confidentialité
                </Link>
              </label>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleRegister}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-lg"
              data-testid="register-btn"
            >
              {loading ? (
                'Création en cours...'
              ) : (
                <>
                  Commencer mon essai gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {/* Features included */}
            <div className="pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 mb-3">Inclus dans votre essai gratuit :</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  '1 licence agent offerte',
                  'Accès complet 7 jours',
                  'Import RNIC & DPE',
                  'Calcul prime CEE',
                  'Pipeline commercial',
                  'Support par ticket'
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Login Link */}
            <p className="text-center text-slate-400 text-sm pt-2">
              Déjà inscrit ?{' '}
              <Link to="/login" className="text-emerald-400 hover:underline font-medium">
                Se connecter
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Pricing Preview */}
        <div className="mt-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Après l'essai gratuit</p>
              <p className="text-white font-bold">À partir de 75€ HT/mois par licence</p>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <CreditCard className="w-5 h-5" />
              <span className="text-sm">Paiement sécurisé Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
